import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  PAYMENT_STATUS_LABEL,
  RECORDED_PAYMENT_ID,
  UNDO_OFFER_MS,
  paymentMethodLabel,
  releaseRefusal,
  takeBackRefusal,
  type AdjustmentKind,
  type AdjustmentMode,
} from '@/domain';
import type { CaseAdjustment, CasePayment, PaymentSummary } from '@/application/ports/FinanceRepository';
import {
  usePaymentSummary,
  useRemoveAdjustment,
  useRemovePayment,
} from '@/application/usecases/useFinance';
import {
  useMarkPaid,
  useReleaseCase,
  useTakeBackRelease,
  useUndoPayment,
} from '@/application/usecases/useOrders';
import { failureWords } from '@/shared/failure/failure';
import { formatDay, formatInstant } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { useMoney } from '@/ui/money/moneyView';
import { StatusDot } from '@/ui/data/StatusDot';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonCase } from '@/ui/feedback/Loading';
import { GoBackIcon, OrdersIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { readFailure, statusOf } from '../clients/clientRefusal';
import { useConfirmAction } from '../shell/useConfirmAction';
import { AdjustmentSheet } from './AdjustmentSheet';
import { PaymentSheet } from './PaymentSheet';
import { ReceiptSheet } from './ReceiptSheet';
import { PAYMENT_STATUS_TONE, adjustmentHeading, adjustmentInWords, signedMoney } from './moneyForm';
import { doctorStatementPath } from './paymentPaths';
import styles from './Finance.module.css';

const t = strings.finance;

/** Which form is open over the page, and for a payment being put right, which. */
type Open =
  | { kind: 'payment' }
  | { kind: 'credit' }
  | { kind: 'adjustment' }
  | { kind: 'prices' }
  | { kind: 'edit'; payment: CasePayment };

/**
 * The payment summary of one case. The request of 8 October 2026 (fourth):
 * "create a clearer and better payment summary pages for cases".
 *
 * One page says everything about a case's money, top to bottom in the order
 * it is asked: what it owes, what has been paid and what is left, in three
 * figures; what can be done about it; the bill, line by line, with every
 * discount and charge and who made it; every payment, with how it came, its
 * reference and its day; and who did what. It replaces nothing: the receipt
 * is still the quick way to mark a case paid from the list, and opens from
 * here to change a price.
 *
 * Everything that changes the bill is asked about first, in the shared
 * confirmation, and said afterwards. What the case may not have done to it is
 * not offered: a paid case takes no payment and no discount, a case with no
 * doctor's record has no credit to use.
 */
export function PaymentSummaryScreen() {
  const { jo } = useParams<{ jo: string }>();
  const summary = usePaymentSummary(jo);
  const money = useMoney();
  const [open, setOpen] = useState<Open | null>(null);

  const back = (
    <Link className={styles.back} to="/payments">
      <GoBackIcon /> {t.backToPayments}
    </Link>
  );

  if (summary.isPending) {
    return (
      <div>
        {back}
        <SkeletonCase label={t.summaryLoading} />
      </div>
    );
  }

  if (summary.isError) {
    return (
      <div>
        {back}
        {statusOf(summary.error) === 404 ? (
          <EmptyState icon={<OrdersIcon />} heading={t.notFound} detail={t.notFoundDetail} />
        ) : (
          <p className={styles.warn} role="alert">
            {readFailure(summary.error)}
          </p>
        )}
      </div>
    );
  }

  const data = summary.data;
  const paid = data.status === 'paid';
  // What the case would owe without the payment being put right: what it may be changed to at the most.
  const withoutIt = (payment: CasePayment) =>
    Math.max(0, Math.round((data.due - (data.paidAmount - payment.amount)) * 100) / 100);

  return (
    <div>
      {back}

      <div className={styles.head} data-testid="summary-head">
        <span className={styles.lbl}>{t.summaryLabel}</span>
        <h2 className={styles.mono}>{data.jobOrderNo}</h2>
        <StatusDot tone={PAYMENT_STATUS_TONE[data.status]}>{PAYMENT_STATUS_LABEL[data.status]}</StatusDot>
      </div>

      <div className={styles.card}>
        <dl className={styles.facts}>
          <div>
            <dt>{t.doctor}</dt>
            <dd>
              {data.doctorId === null ? (
                <span className={styles.unset}>{t.noDoctor}</span>
              ) : (
                <Link to={doctorStatementPath(data.doctorId)} dir="auto">
                  {data.doctorName ?? data.doctorCode}
                </Link>
              )}
            </dd>
          </div>
          {data.patientName != null && (
            <div>
              <dt>{t.patient}</dt>
              <dd dir="auto">{data.patientName}</dd>
            </div>
          )}
          {/* The number on the doctor's paper. The lab's request of 17 October 2026, item 7. */}
          {data.serialNumber != null && (
            <div>
              <dt>{strings.serialNumber.short}</dt>
              <dd>{data.serialNumber}</dd>
            </div>
          )}
          <div>
            <dt>{t.booked}</dt>
            <dd>{formatDay(data.createdAt)}</dd>
          </div>
          <div>
            <dt>{t.dueDate}</dt>
            <dd>{data.deliveryDate}</dd>
          </div>
        </dl>
      </div>

      <div className={styles.figures} role="group" aria-label={t.figuresLabel}>
        <div className={styles.figure}>
          <span>{t.totalDue}</span>
          <b data-testid="summary-due">{money(data.due)}</b>
        </div>
        <div className={styles.figure}>
          <span>{t.paid}</span>
          <b data-testid="summary-paid">{money(data.paidAmount)}</b>
        </div>
        <div className={`${styles.figure} ${data.balance > 0 ? styles.owed : styles.settled}`}>
          <span>{t.balance}</span>
          <b data-testid="summary-balance">{money(data.balance)}</b>
        </div>
      </div>

      {!data.chargeable && <p className={styles.note}>{t.notCharged}</p>}
      {paid && data.paidAt !== null && <p className={styles.note}>{t.paidOn(formatDay(data.paidAt))}</p>}
      {paid && data.balance > 0 && <p className={styles.warn}>{t.owesSinceEdited(money(data.balance))}</p>}
      {!paid && data.credit !== null && data.credit > 0 && (
        <p className={styles.note}>{t.creditAvailable(money(data.credit))}</p>
      )}
      {/* Released to be shipped before it is paid. The lab's request of 17 October 2026, item 8. */}
      {!paid && data.release != null && (
        <p className={styles.note}>{strings.invoice.released(data.release.byName, formatDay(data.release.at))}</p>
      )}

      <Actions data={data} onOpen={setOpen} />

      <section className={styles.card} aria-label={t.billHeading}>
        <div className={styles.section}>
          <h3>{t.billHeading}</h3>
        </div>
        <ul className={styles.rows} aria-label={t.billLines}>
          {data.lines.map((line) => (
            <li key={line.key} className={styles.row}>
              <div className={styles.what}>
                <b>{line.product}</b>
                <span className={styles.teeth}>
                  {t.teeth} {line.teeth.length > 0 ? line.teeth.join(', ') : t.noTeeth}
                </span>
              </div>
              <div className={styles.sum}>
                {money(line.value)}
                <small>
                  {line.perSpan ? t.perSpan : `${String(line.units)} × ${money(line.unitPrice)}`}
                  {line.adjustedFrom !== null && ` · ${t.was} ${money(line.adjustedFrom)}`}
                </small>
              </div>
            </li>
          ))}
        </ul>
        <p className={styles.total}>
          <span>{t.linesTotal}</span>
          <b>{money(data.subtotal)}</b>
        </p>

        <div className={`${styles.section} ${styles.gap}`}>
          <h3>{t.adjustmentsHeading}</h3>
        </div>
        {data.adjustments.length === 0 ? (
          <p className={styles.empty}>{t.noAdjustments}</p>
        ) : (
          <ul className={styles.rows} aria-label={t.adjustmentsHeading}>
            {data.adjustments.map((each) => (
              <AdjustmentRow key={each.id} jobOrderNo={data.jobOrderNo} adjustment={each} locked={paid} />
            ))}
          </ul>
        )}
        <p className={`${styles.total} ${styles.grand}`}>
          <span>{t.totalDue}</span>
          <b>{money(data.due)}</b>
        </p>
      </section>

      <section className={styles.card} aria-label={t.paymentsHeading}>
        <div className={styles.section}>
          <h3>{t.paymentsHeading}</h3>
        </div>
        {data.payments.length === 0 ? (
          <p className={styles.empty}>{t.noPayments}</p>
        ) : (
          <ul className={styles.rows} aria-label={t.paymentsList}>
            {data.payments.map((payment) => (
              <PaymentRow
                key={payment.id}
                jobOrderNo={data.jobOrderNo}
                payment={payment}
                onEdit={() => setOpen({ kind: 'edit', payment })}
              />
            ))}
          </ul>
        )}
        <p className={styles.total}>
          <span>{t.paid}</span>
          <b>{money(data.paidAmount)}</b>
        </p>
        <p className={`${styles.total} ${styles.grand}`}>
          <span>{t.balance}</span>
          <b>{money(data.balance)}</b>
        </p>
      </section>

      <section className={`${styles.card} ${styles.history}`} aria-label={t.historyHeading}>
        <div className={styles.section}>
          <h3>{t.historyHeading}</h3>
        </div>
        {data.history.length === 0 ? (
          <p className={styles.empty}>{t.noHistory}</p>
        ) : (
          <ul className={styles.rows}>
            {data.history.map((entry, index) => (
              // The log has no id here, and two rows may share an instant: its place is its key.
              <li key={`${entry.at}-${String(index)}`} className={styles.row}>
                <div className={styles.what}>
                  <b>{entry.action}</b>
                  <span>{entry.byName ?? ''}</span>
                </div>
                <span className={styles.when}>{formatInstant(entry.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {open?.kind === 'payment' && (
        <PaymentSheet jobOrderNo={data.jobOrderNo} balance={data.balance} onClose={() => setOpen(null)} />
      )}
      {open?.kind === 'credit' && data.credit !== null && (
        <PaymentSheet
          jobOrderNo={data.jobOrderNo}
          balance={data.balance}
          credit={data.credit}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'edit' && (
        <PaymentSheet
          // Another payment is another form.
          key={open.payment.id}
          jobOrderNo={data.jobOrderNo}
          balance={withoutIt(open.payment)}
          payment={open.payment}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'adjustment' && <AdjustmentSheet summary={data} onClose={() => setOpen(null)} />}
      {open?.kind === 'prices' && (
        <ReceiptSheet
          jobOrderNo={data.jobOrderNo}
          onClose={() => setOpen(null)}
          onPaid={(jobOrderNo) => toast(t.nowPaid(jobOrderNo), { tone: 'success' })}
        />
      )}
    </div>
  );
}

/** What can be done about a case's money, and only what can. */
function Actions({ data, onOpen }: { data: PaymentSummary; onOpen: (open: Open) => void }) {
  const markPaid = useMarkPaid();
  const undo = useUndoPayment();
  const release = useReleaseCase();
  const takeBack = useTakeBackRelease();
  const ask = useConfirmAction();
  const paid = data.status === 'paid';
  const owes = data.balance > 0;
  // What a release is decided by, as the summary carries it. The lab's request of 17 October 2026, item 8.
  const releasable = {
    payment: { paid },
    release: data.release ?? null,
    dispatch: { dispatched: data.dispatched === true },
    archive: { archived: false },
  };

  /** Asked first, then said with Undo for about ten seconds: item 10 of the same request. */
  function releaseIt() {
    ask(
      strings.confirm.releaseCase(data.jobOrderNo),
      () =>
        release.mutateAsync(data.jobOrderNo).then(() =>
          toast(strings.payments.released(data.jobOrderNo), {
            tone: 'success',
            durationMs: UNDO_OFFER_MS,
            action: {
              label: strings.undo.action,
              onClick: () => {
                takeBack.mutateAsync(data.jobOrderNo).then(
                  () => toast(strings.payments.releaseTakenBack(data.jobOrderNo)),
                  (error: Error) => toast(failureWords(error), { tone: 'error' }),
                );
              },
            },
          }),
        ),
      { confirmLabel: strings.payments.releaseToShip },
    );
  }

  function takeItBack() {
    ask(
      strings.confirm.takeBackRelease(data.jobOrderNo),
      () =>
        takeBack
          .mutateAsync(data.jobOrderNo)
          .then(() => toast(strings.payments.releaseTakenBack(data.jobOrderNo), { tone: 'success' })),
      { confirmLabel: strings.payments.takeBackRelease },
    );
  }

  return (
    <div className={styles.actions} role="group" aria-label={t.actionsLabel}>
      {!paid && owes && (
        <Button variant="primary" onClick={() => onOpen({ kind: 'payment' })}>
          {t.addPayment}
        </Button>
      )}
      {!paid && owes && data.credit !== null && data.credit > 0 && (
        <Button onClick={() => onOpen({ kind: 'credit' })}>{t.useCredit}</Button>
      )}
      {/* A case that owes nothing is paid by saying so: there is no money to record. */}
      {!paid && !owes && (
        <Button
          variant="primary"
          loading={markPaid.isPending}
          onClick={() =>
            ask(
              strings.confirm.markPaidNothingOwed(data.jobOrderNo),
              () =>
                markPaid
                  .mutateAsync(data.jobOrderNo)
                  .then(() => toast(t.nowPaid(data.jobOrderNo), { tone: 'success' })),
              { confirmLabel: t.markPaid },
            )
          }
        >
          {t.markPaid}
        </Button>
      )}
      {/* To be shipped before it is paid, and taken back while it is still in the lab. */}
      {data.release !== undefined && releaseRefusal(releasable) === null && (
        <Button loading={release.isPending} onClick={releaseIt}>
          {strings.payments.releaseToShip}
        </Button>
      )}
      {data.release != null && !paid && takeBackRefusal(releasable) === null && (
        <Button loading={takeBack.isPending} onClick={takeItBack}>
          {strings.payments.takeBackRelease}
        </Button>
      )}
      {!paid && data.chargeable && <Button onClick={() => onOpen({ kind: 'adjustment' })}>{t.addAdjustment}</Button>}
      {!paid && data.chargeable && <Button onClick={() => onOpen({ kind: 'prices' })}>{t.prices}</Button>}
      {/* Paid with no payment to remove: marked so by hand, and unmarked the same way. */}
      {paid && data.payments.length === 0 && (
        <Button
          loading={undo.isPending}
          onClick={() =>
            ask(
              strings.confirm.markUnpaid(data.jobOrderNo),
              () =>
                undo
                  .mutateAsync(data.jobOrderNo)
                  .then(() => toast(`${data.jobOrderNo} ${strings.payments.paymentUndone}`, { tone: 'success' })),
              { confirmLabel: t.markUnpaid },
            )
          }
        >
          {t.markUnpaid}
        </Button>
      )}
      {data.doctorId !== null && (
        <Link className={styles.linkButton} to={doctorStatementPath(data.doctorId)}>
          {t.statementLink}
        </Link>
      )}
    </div>
  );
}

function AdjustmentRow({
  jobOrderNo,
  adjustment,
  locked,
}: {
  jobOrderNo: string;
  adjustment: CaseAdjustment;
  /** A paid case's bill is not changed: a payment is removed first. */
  locked: boolean;
}) {
  const remove = useRemoveAdjustment();
  const ask = useConfirmAction();
  const money = useMoney();
  const kind: AdjustmentKind = adjustment.kind;
  const mode: AdjustmentMode = adjustment.mode;
  const heading = adjustmentHeading(kind, mode, adjustment.value, money);

  return (
    <li className={styles.row}>
      <div className={styles.what}>
        <b>{heading}</b>
        <span>{adjustment.reason}</span>
        {adjustment.at !== undefined && (
          <span>{t.by(adjustment.byName ?? null, formatDay(adjustment.at))}</span>
        )}
      </div>
      <div className={`${styles.sum} ${adjustment.amount < 0 ? styles.minus : ''}`}>{signedMoney(adjustment.amount, money)}</div>
      {!locked && (
        <div className={styles.rowActions}>
          <button
            type="button"
            className={styles.mini}
            disabled={remove.isPending}
            aria-label={t.removeAdjustmentLabel(heading)}
            onClick={() =>
              ask(
                strings.confirm.removeAdjustment(jobOrderNo, adjustmentInWords(kind, mode, adjustment.value, money)),
                () =>
                  remove
                    .mutateAsync({ jobOrderNo, id: adjustment.id })
                    .then(() => toast(t.adjustmentRemoved, { tone: 'success' })),
                { confirmLabel: t.remove },
              )
            }
          >
            {t.remove}
          </button>
        </div>
      )}
    </li>
  );
}

function PaymentRow({
  jobOrderNo,
  payment,
  onEdit,
}: {
  jobOrderNo: string;
  payment: CasePayment;
  onEdit: () => void;
}) {
  const remove = useRemovePayment();
  const ask = useConfirmAction();
  const money = useMoney();
  const fromCredit = payment.method === 'credit';
  const amount = money(payment.amount);
  const details = [payment.reference, payment.note].filter((each): each is string => each !== null && each !== '');

  return (
    <li className={styles.row} data-payment={payment.id === RECORDED_PAYMENT_ID ? 'recorded' : undefined}>
      <div className={styles.what}>
        <b>{paymentMethodLabel(payment.method)}</b>
        {details.length > 0 && <span>{details.join(' · ')}</span>}
        <span>{t.by(payment.byName, formatDay(payment.paidAt))}</span>
      </div>
      <div className={styles.sum}>{amount}</div>
      <div className={styles.rowActions}>
        {/* Credit used is removed, not changed: it goes back to the doctor and is used again. */}
        {!fromCredit && (
          <button type="button" className={styles.mini} aria-label={t.editPaymentLabel(amount)} onClick={onEdit}>
            {t.edit}
          </button>
        )}
        <button
          type="button"
          className={styles.mini}
          disabled={remove.isPending}
          aria-label={t.removePaymentLabel(amount)}
          onClick={() =>
            ask(
              strings.confirm.removePayment(jobOrderNo, amount, fromCredit),
              () =>
                remove
                  .mutateAsync({ jobOrderNo, id: payment.id })
                  .then(() => toast(t.paymentRemoved, { tone: 'success' })),
              { confirmLabel: t.remove },
            )
          }
        >
          {t.remove}
        </button>
      </div>
    </li>
  );
}
